import { ComponentProps } from 'react';
import i18n from 'i18next';

import { opTime } from 'utils/dates';
import { OpUpload } from 'new-components/DLS/OpUpload/OpUpload';
import { notificationService } from 'containers/AlertWrapper/notificationService';

export const capitalizeName = (name: string | null) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const formatFullName = (
  firstName: string | null | undefined,
  middleName: string | null | undefined,
  lastName: string | null | undefined,
) => {
  const first = capitalizeName(firstName ?? null)?.trim();
  const middle = capitalizeName(middleName ?? null)?.trim();
  const last = capitalizeName(lastName ?? null)?.trim();
  return [first, middle, last].filter(Boolean).join(' ');
};

export const maskName = (name: string | null | undefined): string => {
  if (!name) return '';

  const characters = Array.from(name);
  const length = characters.length;

  if (length === 1) {
    return name;
  }

  if (length === 2) {
    characters[1] = '*';
    return characters.join('');
  }

  if (length === 3) {
    characters[2] = '*';
    return characters.join('');
  }

  const startIdx = Math.floor(length / 2);
  for (let i = startIdx; i < length; i++) {
    characters[i] = '*';
  }

  return characters.join('');
};

export function transformNullToEmptyString<T extends object>(obj: T): T {
  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    const value = (obj as Record<string, unknown>)[key];
    let transformedValue: unknown;

    // Check if the value is a Day.js object
    if (opTime.isDayjs(value)) {
      transformedValue = value; // Keep the Day.js object as is
    } else if (value === null) {
      transformedValue = ''; // Replace null with an empty string
    } else if (value === true) {
      transformedValue = 1; // Replace true with 1
    } else if (value === false) {
      transformedValue = 0; // Replace false with 0
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // If the value is an object (but not an array), recursively transform it
      transformedValue = transformNullToEmptyString(
        value as Record<string, unknown>,
      );
    } else {
      transformedValue = value; // Keep the value as is
    }
    return {
      ...acc,
      [key]: transformedValue,
    };
  }, {}) as T;
}

export const hasPermission = (
  tokenScopeList: Array<{
    org: { id: number | string | null };
    scope: Array<string | number>;
  }>,
  orgId: number,
  prefix: 'o' | 's',
  permission: string,
) => {
  const orgPermissionPrefix = `${prefix}${orgId}-`;
  const requiredPermission = orgPermissionPrefix + permission;

  // Filter the scopes that belong to the given orgId
  const orgScopes = tokenScopeList.find(
    (scope) => Number(scope.org.id) === Number(orgId),
  );
  if (orgScopes) {
    return orgScopes.scope.includes(requiredPermission);
  }

  return false;
};

export const statusKeyMap: Record<string, string> = {
  Pending: 'status.pending',
  'Signed In': 'status.signedIn',
  'Signed Out': 'status.signedOut',
  'Denied Entry': 'status.deniedEntry',
  'No Show': 'status.noShow',
};

/**
 * Verify file signature (magic bytes) to prevent file type spoofing.
 * Checks actual binary header instead of relying on MIME type or extension.
 */
const verifyFileSignature = async (file: File): Promise<boolean> => {
  try {
    const buffer = await file.slice(0, 8).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // PNG signature: 89 50 4E 47 0D 0A 1A 0A
    if (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return true;
    }

    // JPEG signature: FF D8 FF
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

export const resizeImage = (
  file: File,
  maxSize: number,
  quality: number,
): Promise<File | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target!.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          }
        } else if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            resolve(
              new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }),
            );
          },
          'image/jpeg',
          quality,
        );
      };
    };
  });
};

export const validateFile = async (file: File): Promise<File | null> => {
  // Step 1: Check MIME type (basic client-side filter)
  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    notificationService.show(
      'error',
      i18n.t('util.notification.invalidFileError'),
    );
    return null;
  }

  // Step 2: Verify actual file signature (magic bytes) to prevent spoofing
  const isValidSignature = await verifyFileSignature(file);
  if (!isValidSignature) {
    notificationService.show(
      'error',
      i18n.t('util.notification.invalidFileError'),
    );
    return null;
  }

  // Step 3: Resize if file is too large
  if (file.size > 1048576) {
    return resizeImage(file, 600, 0.7);
  }

  return file;
};

export const beforeUpload = (
  sizeByte: number,
  sizeMegaByte: string,
): NonNullable<ComponentProps<typeof OpUpload>['beforeUpload']> => {
  return async (file) => {
    if (file.size > sizeByte) {
      notificationService.show(
        'warning',
        i18n.t('util.notification.fileSizeWarning', { size: sizeMegaByte }),
      );
      return false;
    }

    await validateFile(file);
    return false;
  };
};
